import { defineStore } from "pinia";
import { computed, ref } from "vue";

import {
  getSources,
  type Medium,
  type MediumField,
} from "shared/helpers/media";
import OrganisationsService, {
  type OrganisationFeatures,
} from "shared/services/api/OrganisationsService";
import { type FeatureKey } from "shared/types/features";
import { type MentionType } from "shared/types/mentions";

export type {
  OrganisationFeature,
  OrganisationFeatures,
} from "shared/services/api/OrganisationsService";

export type OrganisationFeaturesContentToggle = {
  [key in `selected_${MediumField}_content`]?: MentionType[];
};

const defaultHandleErrors = true;

export const useOrganisationFeaturesStore = defineStore(
  "organisationFeatures",
  () => {
    const organisationFeatures = ref<OrganisationFeatures>({});

    const enabledSources = computed<Medium[]>(() =>
      getSources().filter(
        (source) => source.filterEnabled && hasFeature(source.featureFlag)
      )
    );

    const defaultContentToggles = computed<OrganisationFeaturesContentToggle>(
      () => {
        const defaults: OrganisationFeaturesContentToggle = {};

        enabledSources.value.forEach((source) => {
          if (hasFeature(source.featureFlag)) {
            defaults[`selected_${source.field}_content`] = source.relatedTypes
              .filter(
                (type) =>
                  (!type.featureFlag || hasFeature(type.featureFlag)) &&
                  !type.hidden
              )
              .map(({ field }) => field);
          }
        });

        return defaults;
      }
    );

    const defaultCheckedContentToggles =
      computed<OrganisationFeaturesContentToggle>(() => {
        const defaults: OrganisationFeaturesContentToggle = {};

        enabledSources.value.forEach((source) => {
          if (hasFeature(source.featureFlag)) {
            defaults[`selected_${source.field}_content`] = source.relatedTypes
              .filter(
                (type) =>
                  type.checked &&
                  (!type.featureFlag || hasFeature(type.featureFlag))
              )
              .map(({ field }) => field);
          }
        });

        return defaults;
      });

    const socialEngage = computed(
      () => organisationFeatures.value.has_social_engage ?? null
    );

    function hasFeature(feature: FeatureKey) {
      return feature in organisationFeatures.value;
    }

    function getOrganisationFeatures({
      organisationId,
    }: {
      organisationId: number;
    }) {
      return OrganisationsService.features(organisationId, {
        handleErrors: defaultHandleErrors,
      })
        .then((response) => {
          updateOrganisationFeatures(response.data);
        })
        .catch((error) => error);
    }

    function refreshUsedCredits({
      feature,
      handleErrors,
      organisationId,
    }: {
      feature: FeatureKey;
      handleErrors?: boolean;
      organisationId: number;
    }) {
      let shouldHandleErrors = handleErrors;

      if (shouldHandleErrors === undefined) {
        shouldHandleErrors = defaultHandleErrors;
      }

      return OrganisationsService.feature(organisationId, feature, {
        handleErrors: shouldHandleErrors,
      }).then((response) => {
        updateOrganisationFeatures({
          [feature]: response.data,
        });
      });
    }

    function updateOrganisationFeatures(data: OrganisationFeatures) {
      organisationFeatures.value = { ...organisationFeatures.value, ...data };
    }

    function resetOrganisationFeatures(data: OrganisationFeatures) {
      organisationFeatures.value = { ...data };
    }

    return {
      defaultCheckedContentToggles,

      defaultContentToggles,
      enabledSources,
      getOrganisationFeatures,
      hasFeature,

      organisationFeatures,
      refreshUsedCredits,
      resetOrganisationFeatures,
      socialEngage,
      updateOrganisationFeatures,
    };
  }
);

export default useOrganisationFeaturesStore;
